import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


const FIXED_EDGE_VARIANTS = [
  { edge: 4, freeSum: 11 }, // oryginal: suma z B = 15
  { edge: 2, freeSum: 9 },
  { edge: 3, freeSum: 10 },
  { edge: 3, freeSum: 12 },
  { edge: 4, freeSum: 10 },
  { edge: 4, freeSum: 12 },
  { edge: 5, freeSum: 10 },
  { edge: 5, freeSum: 12 },
  { edge: 5, freeSum: 14 },
  { edge: 6, freeSum: 14 },
  { edge: 6, freeSum: 16 },
  { edge: 7, freeSum: 16 },
  { edge: 8, freeSum: 18 },
];

const RATIO_EDGE_VARIANTS = [
  { ratio: 2, sumAtVertex: 14 },
  { ratio: 2, sumAtVertex: 28 },
  { ratio: 3, sumAtVertex: 13 },
  { ratio: 3, sumAtVertex: 26 },
  { ratio: 3, sumAtVertex: 12 },
  { ratio: 4, sumAtVertex: 21 },
];

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

function fracParts(num, den = 1) {
  if (den < 0) return fracParts(-num, -den);
  const g = gcd(num, den);
  return [num / g, den / g];
}

function fracLatex(num, den = 1) {
  const [n, d] = fracParts(num, den);
  if (d === 1) return String(n);
  return `\\frac{${n}}{${d}}`;
}

function signedTerm(coef, variable = "x") {
  if (coef === 0) return "";
  const sign = coef > 0 ? "+" : "-";
  const abs = Math.abs(coef);
  const body = abs === 1 ? variable : `${abs}${variable}`;
  return `${sign}${body}`;
}

function polynomialLatex(data) {
  const aPart = data.aCoef === -1 ? "-x^2" : data.aCoef === 1 ? "x^2" : `${data.aCoef}x^2`;
  const bPart = signedTerm(data.bCoef, "x");
  const cPart = data.constant ? signedTerm(data.constant, "") : "";
  return `${aPart}${bPart}${cPart}`.replace(/\+-/g, "-").replace(/^\+/, "");
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (value.raw && typeof value.raw === "object" && typeof value.raw.raw === "string") {
      return value.raw.raw;
    }
    if (typeof value.raw === "string") return value.raw;
    for (const key of ["value", "text", "input", "expr", "equation", "latex"]) {
      if (typeof value[key] === "string") return value[key];
    }
  }
  return String(value);
}

function normalize(raw) {
  return rawValue(raw)
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot|\\times|·|×/g, "*")
    .replace(/\\,/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–—]/g, "-")
    .replace(/²/g, "^2")
    .replace(/³/g, "^3")
    .replace(/¹/g, "^1")
    .replace(/\{([^{}]+)\}/g, "$1");
}

function parseNumber(raw) {
  const s = normalize(raw);
  if (!s) return null;
  const frac = s.match(/^([+-]?\d+)\/([+-]?\d+)$/);
  if (frac) {
    const n = Number(frac[1]);
    const d = Number(frac[2]);
    if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return null;
    return n / d;
  }
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
}

function eq(a, b, eps = 1e-8) {
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= eps;
}

function sameInterval(user, expected) {
  if (!user) return false;
  return (
    eq(user.left, expected.left) &&
    eq(user.right, expected.right) &&
    user.leftClosed === expected.leftClosed &&
    user.rightClosed === expected.rightClosed
  );
}

function formulaRhs(raw) {
  let s = normalize(raw)
    .replace(/p\(x\)/g, "p")
    .replace(/p_x/g, "p");
  if (s.includes("=")) {
    const parts = s.split("=");
    if (parts.length !== 2) return "";
    s = parts[0].includes("p") ? parts[1] : parts[0];
  }
  return s;
}

function latexFractionsToPlain(s) {
  let out = s;
  let prev;
  do {
    prev = out;
    out = out
      .replace(/\\dfrac/g, "\\frac")
      .replace(/\\frac\{?([+-]?\d+)\}?\{?([+-]?\d+)\}?/g, "($1/$2)");
  } while (out !== prev);
  return out;
}

function toJsExpression(raw) {
  let s = latexFractionsToPlain(formulaRhs(raw));
  if (!s) return "";
  s = s.replace(/\^/g, "**");
  s = s.replace(/-x\*\*(\([^)]*\)|\d+(?:\.\d+)?)/g, "-(x**$1)");
  // Allow common typed forms like -2(x^2-14x-70) or 2[-x^2+14x+70].
  s = s.replace(/\[/g, "(").replace(/\]/g, ")");
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/x\(/g, "x*(");
  s = s.replace(/\)(x|\d|\()/g, ")*$1");
  if (!/^[0-9x+\-*/().*]+$/.test(s)) return "";
  return s;
}

function evalFormula(raw, x) {
  const expr = toJsExpression(raw);
  if (!expr) return null;
  try {
    const value = Function("x", `return (${expr});`)(x);
    return Number.isFinite(value) ? value : null;
  } catch (_err) {
    return null;
  }
}

function expectedP(data, x) {
  return data.aCoef * x * x + data.bCoef * x + data.constant;
}

function checkFormula(raw, data) {
  const right = data.domainRight;
  const samples = [
    Math.max(0.5, right / 5),
    Math.max(1, right / 3),
    Math.max(1.5, right / 2),
    Math.max(2, (2 * right) / 3),
  ];
  return samples.every((x) => eq(evalFormula(raw, x), expectedP(data, x), 1e-6));
}

function relationExpressionToJs(expr, data) {
  let s = normalize(expr);
  s = s
    .replace(/bf/g, data.mode === "fixed" ? "y" : "bf")
    .replace(/bc/g, data.mode === "ratio" ? "y" : String(data.edge || "bc"))
    .replace(/ab/g, "x")
    .replace(/ae/g, data.mode === "ratio" ? `${data.ratio}x` : "ae");
  s = s.replace(/(\d)([a-z])/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/([a-z])\(/g, "$1*(");
  s = s.replace(/\)([a-z]|\d|\()/g, ")*$1");
  return s;
}

function evaluateLinearExpression(expr, values) {
  const js = relationExpressionToJs(expr, values.__data);
  if (!js || !/^[0-9a-z+\-*/().]+$/.test(js)) return null;
  const vars = Array.from(new Set(js.match(/[a-z]+/g) || []));
  if (vars.some((v) => values[v] == null)) return null;
  try {
    const result = Function(...vars, `return (${js});`)(...vars.map((v) => values[v]));
    return Number.isFinite(result) ? result : null;
  } catch (_err) {
    return null;
  }
}

function checkRelation(raw, data) {
  const prepared = normalize(raw);
  if (!prepared.includes("=")) return false;
  const parts = prepared.split("=");
  if (parts.length !== 2) return false;

  const leftForVars = relationExpressionToJs(parts[0], data);
  const rightForVars = relationExpressionToJs(parts[1], data);
  const variables = Array.from(new Set(`${leftForVars}${rightForVars}`.match(/[a-z]+/g) || []));
  const otherVars = variables.filter((v) => v !== "x");
  if (otherVars.length !== 1) return false;
  const missing = otherVars[0];

  const expectedXCoef = data.mode === "ratio" ? data.ratio + 1 : 1;
  const expectedRight = data.freeSum;
  const samples = [
    { x: 1, [missing]: 2 },
    { x: 2, [missing]: 5 },
    { x: 3, [missing]: 1 },
    { x: 4, [missing]: 7 },
  ];

  let scalar = null;
  for (const sample of samples) {
    const values = { ...sample, __data: data };
    const actualLeft = evaluateLinearExpression(parts[0], values);
    const actualRight = evaluateLinearExpression(parts[1], values);
    if (actualLeft == null || actualRight == null) return false;
    const actualDiff = actualLeft - actualRight;
    const expectedDiff = expectedXCoef * sample.x + sample[missing] - expectedRight;
    if (Math.abs(expectedDiff) < 1e-9) {
      if (Math.abs(actualDiff) > 1e-7) return false;
      continue;
    }
    const nextScalar = actualDiff / expectedDiff;
    if (Math.abs(nextScalar) < 1e-9) return false;
    if (scalar == null) scalar = nextScalar;
    else if (!eq(nextScalar, scalar, 1e-7)) return false;
  }
  return scalar != null;
}

function userLatex(raw, prefix = "") {
  const value = rawValue(raw).trim();
  if (!value) return null;
  const body = formatLatexPreview(value, "") || value;
  return `${prefix}${body}`;
}

function CuboidSketch({ edge, showEdge }) {
  const label = (math, x, y) => (
    <foreignObject key={math} x={x} y={y - 23} width="36" height="32">
      <div style={{ color: "var(--text-color, #f8fafc)", fontSize: 18 }}><InlineMath math={String(math)} /></div>
    </foreignObject>
  );
  return (
    <svg width="360" height="360" viewBox="0 0 360 360" role="img" aria-label="Prostopadłościan ABCDEFGH" style={{ display: "block", width: "100%", maxWidth: 360, height: "auto" }}>
      <defs>
        <style>{`
          .cuboid-line { stroke: var(--text-color, #f8fafc); stroke-width: 2; fill: none; }
          .cuboid-hidden { stroke: var(--text-color, #f8fafc); stroke-width: 1.7; stroke-dasharray: 6 5; fill: none; opacity: .8; }
        `}</style>
      </defs>
      <path className="cuboid-line" d="M82 314 L210 314 L210 109 L82 109 Z" />
      <path className="cuboid-line" d="M82 109 L126 56 L254 56 L210 109" />
      <path className="cuboid-line" d="M210 314 L254 261 L254 56" />
      <path className="cuboid-hidden" d="M82 314 L126 261 L254 261" />
      <path className="cuboid-hidden" d="M126 261 L126 56" />

      {[["A",67,337],["B",214,337],["C",262,267],["D",105,255],["E",64,105],["F",218,123],["G",260,56],["H",110,51],["x",142,344]].map(([math,x,y]) => label(math,x,y))}
      {showEdge ? label(edge,241,299) : null}
    </svg>
  );
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 31:
 *   prostopadłościan, BC=4, suma krawędzi z B = 15 (więc AB=x, BF=11−x).
 *   P(x) = 2(4x + x(11−x) + 4(11−x)) = −2x² + 22x + 88, D=(0;11), max przy x=11/2.
 */
function createMay2025OriginalTask() {
  const edge = 4;
  const freeSum = 11;
  const total = edge + freeSum; // 15

  return {
    type: "MaturaCuboidSurfaceOptimizationOpenTask",
    data: {
      mode: "fixed",
      sumMode: "vertex",
      edge,
      freeSum,
      sumAtVertex: total,
      totalAllEdges: 4 * total,
      aCoef: -2,
      bCoef: 2 * freeSum,
      constant: 2 * edge * freeSum,
      xMaxNum: freeSum,
      xMaxDen: 2,
      domainRightNum: freeSum,
      domainRightDen: 1,
      domainRight: freeSum,
      maxPoints: 4,
      expectedDomain: {
        left: 0,
        right: freeSum,
        leftClosed: false,
        rightClosed: false,
      },
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 31,
    },
  };
}

export function buildRatioCuboidSurfaceData(ratio, freeSum, sumMode = "all") {
  const quad=ratio*ratio+ratio+1;
  return {mode:"ratio",sumMode,ratio,freeSum,sumAtVertex:freeSum,totalAllEdges:4*freeSum,
    aCoef:-2*quad,bCoef:2*(ratio+1)*freeSum,constant:0,xMaxNum:(ratio+1)*freeSum,xMaxDen:2*quad,
    domainRightNum:freeSum,domainRightDen:ratio+1,domainRight:freeSum/(ratio+1),maxPoints:4,
    expectedDomain:{left:0,right:freeSum/(ratio+1),leftClosed:false,rightClosed:false}};
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return createMay2025OriginalTask();
  }

  const mode = Math.random() < 0.5 ? "fixed" : "ratio";
  const sumMode = Math.random() < 0.5 ? "vertex" : "all";

  let data;
  if (mode === "fixed") {
    const picked = pickOne(FIXED_EDGE_VARIANTS);
    const edge = picked.edge;
    const freeSum = picked.freeSum;
    const total = edge + freeSum;
    data = {
      mode,
      sumMode,
      edge,
      freeSum,
      sumAtVertex: total,
      totalAllEdges: 4 * total,
      aCoef: -2,
      bCoef: 2 * freeSum,
      constant: 2 * edge * freeSum,
      xMaxNum: freeSum,
      xMaxDen: 2,
      domainRightNum: freeSum,
      domainRightDen: 1,
      domainRight: freeSum,
      maxPoints: 4,
    };
  } else {
    const picked = pickOne(RATIO_EDGE_VARIANTS);
    const ratio = picked.ratio;
    const freeSum = picked.sumAtVertex;
    data = buildRatioCuboidSurfaceData(ratio, freeSum, sumMode);
  }

  data.expectedDomain = {
    left: 0,
    right: data.domainRight,
    leftClosed: false,
    rightClosed: false,
  };

  return {
    type: "MaturaCuboidSurfaceOptimizationOpenTask",
    data,
  };
}

export function renderTask({ data }) {
  const isFixed = data.mode === "fixed";
  const sumDescription = data.sumMode === "all"
    ? "suma długości wszystkich dwunastu krawędzi"
    : <>suma długości wszystkich krawędzi wychodzących z wierzchołka <InlineMath math="B" /></>;
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - otwarte (0-4 pkt).</strong>
      </p>
      {isFixed ? (
        <p>
          Rozważamy wszystkie prostopadłościany <InlineMath math="ABCDEFGH" />, w których krawędź{" "}
          <InlineMath math="BC" /> ma długość <InlineMath math={String(data.edge)} /> oraz {sumDescription} jest równa{" "}
          <InlineMath math={String(data.sumMode === "all" ? data.totalAllEdges : data.sumAtVertex)} />.
        </p>
      ) : (
        <p>
          Rozważamy wszystkie prostopadłościany <InlineMath math="ABCDEFGH" />, w których krawędź{" "}
          <InlineMath math="AE" /> jest <InlineMath math={String(data.ratio)} /> razy dłuższa od krawędzi{" "}
          <InlineMath math="AB" /> oraz {sumDescription} jest równa{" "}
          <InlineMath math={String(data.sumMode === "all" ? data.totalAllEdges : data.sumAtVertex)} />.
        </p>
      )}
      <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
        <CuboidSketch edge={data.edge} showEdge={isFixed} />
      </div>
      <p>
        Niech <InlineMath math="P(x)" /> oznacza funkcję pola powierzchni całkowitej takiego
        prostopadłościanu w zależności od długości <InlineMath math="x" /> krawędzi <InlineMath math="AB" />.
      </p>
      <p>
        Wyznacz wzór i dziedzinę funkcji <InlineMath math="P" />. Oblicz długość <InlineMath math="x" />
        krawędzi <InlineMath math="AB" /> tego z rozważanych prostopadłościanów, którego pole powierzchni
        całkowitej jest największe. Zapisz obliczenia.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const set = (key) => (value) => onChange({ ...ua, [key]: value });

  return (
    <div className="space-y-4" style={{ maxWidth: 620 }}>
      <OpenTaskScoringHint variant="independent" />
      <div>
        <div style={{ marginBottom: 6 }}>
          <strong>Krok 1.</strong> Oznacz brakującą krawędź wychodzącą z wierzchołka <InlineMath math="B" /> i zapisz zależność:
        </div>
        <EquationInput
          value={ua.relation}
          onChange={set("relation")}
          disabled={disabled}
          placeholder="np. zależność z sumy krawędzi"
          showPreview={false}
        />
      </div>

      <div>
        <div style={{ marginBottom: 6 }}>
          <strong>Krok 2.</strong> Zapisz dziedzinę funkcji <InlineMath math="P" />:
        </div>
        <IntervalInput
          value={ua.domain ?? ""}
          onChange={set("domain")}
          disabled={disabled}
          asString
          previewWithX
          prefixAbove
          placeholder="np. (0; a)"
        />
      </div>

      <div>
        <div style={{ marginBottom: 6 }}>
          <strong>Krok 3.</strong> Zapisz wzór funkcji pola powierzchni całkowitej:
        </div>
        <EquationInput
          value={ua.formula}
          onChange={set("formula")}
          disabled={disabled}
          placeholder="np. P(x)=ax^2+bx+c"
          showPreview={false}
        />
      </div>

      <div>
        <div style={{ marginBottom: 6 }}>
          <strong>Krok 4.</strong> Podaj długość krawędzi <InlineMath math="AB" />, dla której pole jest największe:{" "}
          <InlineMath math="x=" />
        </div>
        <FractionInput
          value={ua.xMax}
          onChange={set("xMax")}
          disabled={disabled}
          label="x"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const relationOk = checkRelation(ua.relation, data);

  const parsedDomain = parseIntervalInput(rawValue(ua.domain));
  const domainOk =
    parsedDomain?.kind === "intervals" &&
    parsedDomain.intervals.length === 1 &&
    sameInterval(parsedDomain.intervals[0], data.expectedDomain);

  const formulaOk = checkFormula(ua.formula, data);
  const xMax = parseNumber(ua.xMax);
  const xMaxOk = xMax != null && eq(xMax, data.xMaxNum / data.xMaxDen);

  const score = [relationOk, domainOk, formulaOk, xMaxOk].filter(Boolean).length;

  return {
    isCorrect: score === data.maxPoints,
    score,
    maxScore: data.maxPoints,
    parts: {
      relation: relationOk,
      domain: domainOk,
      formula: formulaOk,
      maximum: xMaxOk,
    },
  };
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const relation = userLatex(userAnswer.relation);
  const domainRaw = rawValue(userAnswer.domain);
  const domain = domainRaw ? intervalToLatex(domainRaw, { withX: true }) : null;
  const formula = userLatex(userAnswer.formula);
  const xMax = userLatex(userAnswer.xMax, "x=");

  return (
    <div className="space-y-1">
      {relation ? <div><InlineMath math={relation} /></div> : null}
      {domain ? <div><InlineMath math={domain} /></div> : null}
      {formula ? <div><InlineMath math={formula} /></div> : null}
      {xMax ? <div><InlineMath math={xMax} /></div> : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  const domainLatex = `(0;\\,${fracLatex(data.domainRightNum, data.domainRightDen)})`;
  const xMaxLatex = fracLatex(data.xMaxNum, data.xMaxDen);
  const relationLatex = data.mode === "fixed"
    ? (data.sumMode === "all" ? `4(x+${data.edge}+y)=${data.totalAllEdges}` : `x+${data.edge}+y=${data.sumAtVertex}`)
    : (data.sumMode === "all" ? `4(x+${data.ratio}x+y)=${data.totalAllEdges}` : `x+${data.ratio}x+y=${data.sumAtVertex}`);
  return (
    <div className="space-y-1">
      <div><InlineMath math={relationLatex} /></div>
      <div><InlineMath math={`D=${domainLatex}`} /></div>
      <div><InlineMath math={`P(x)=${polynomialLatex(data)}`} /></div>
      <div><InlineMath math={`x=${xMaxLatex}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const xMaxLatex = fracLatex(data.xMaxNum, data.xMaxDen);
  const domainRightLatex = fracLatex(data.domainRightNum, data.domainRightDen);
  const relationStartLatex = data.mode === "fixed"
    ? `AB=x,\\quad BC=${data.edge},\\quad BF=y`
    : `AB=x,\\quad BF=${data.ratio}x,\\quad BC=y`;
  const relationLatex = data.mode === "fixed"
    ? `x+${data.edge}+y=${data.sumAtVertex}`
    : `x+${data.ratio}x+y=${data.sumAtVertex}`;
  const yLatex = data.mode === "fixed"
    ? `${data.freeSum}-x`
    : `${data.freeSum}-${data.ratio + 1}x`;
  const surfaceSubLatex = data.mode === "fixed"
    ? `\\begin{aligned}P(x)=2\\bigl[&${data.edge}x+x(${data.freeSum}-x)\\\\&+${data.edge}(${data.freeSum}-x)\\bigr]\\end{aligned}`
    : `P(x)=2\\left(xy+x\\cdot ${data.ratio}x+y\\cdot ${data.ratio}x\\right)`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Oznaczamy długości krawędzi wychodzących z wierzchołka <InlineMath math="B" />.
      </p>
      <BlockMath math={relationStartLatex} />
      {data.mode === "ratio" ? <p>Krawędzie <InlineMath math="AE" /> i <InlineMath math="BF" /> mają tę samą długość.</p> : null}
      {data.sumMode === "all" ? (
        <>
          <BlockMath math={`4(${relationLatex.split("=")[0]})=${data.totalAllEdges}`} />
          <BlockMath math={relationLatex} />
        </>
      ) : (
        <BlockMath math={relationLatex} />
      )}
      <BlockMath math={`y=${yLatex}`} />

      <p>
        <strong>Krok 2.</strong> Wyznaczamy dziedzinę. Obie zmienne długości muszą być dodatnie.
      </p>
      <BlockMath math={`x>0\\quad\\text{oraz}\\quad ${yLatex}>0`} />
      <BlockMath math={`D=(0;\\,${domainRightLatex})`} />

      <p>
        <strong>Krok 3.</strong> Zapisujemy pole powierzchni całkowitej prostopadłościanu.
      </p>
      <BlockMath math="P=2(ab+ac+bc)" />
      <BlockMath math={surfaceSubLatex} />
      {data.mode === "ratio" ? <BlockMath math={`y=${yLatex}`} /> : null}
      <BlockMath math={`P(x)=${polynomialLatex(data)}`} />

      <p>
        <strong>Krok 4.</strong> Funkcja <InlineMath math="P" /> jest kwadratowa i ma ujemny współczynnik przy <InlineMath math="x^2" />,
        więc największą wartość przyjmuje w wierzchołku paraboli.
      </p>
      <BlockMath math={`x_w=-\\frac{b}{2a}=${xMaxLatex}`} />
      <p>Wierzchołek należy do dziedziny, ponieważ <InlineMath math={`0<${xMaxLatex}<${domainRightLatex}`} />.</p>
      <p>
        Szukana długość krawędzi <InlineMath math="AB" /> wynosi <InlineMath math={xMaxLatex} />.
      </p>
    </div>
  );
}
